/**
 * Production-ready reimagined Loan Summary component.
 *
 * Renders inside the existing `account-activity.accounts` route
 * when the `reimaginedAccountActivity` feature flag is enabled.
 *
 * Strips out: demo mode, DataIndicator badges, LRAP discovery banner,
 * AI advisor action, and links to reimagined-only routes.
 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { hash } from '@ember/helper';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import { eq } from 'tio-ui/utilities';
import StatCard from 'tio-reimagined/components/stat-card';
import SectionHeader from 'tio-reimagined/components/section-header';
import NavCard from 'tio-reimagined/components/nav-card';
import Button from 'tio-reimagined/components/button';
import DebtJourneyHero from 'tio-employee/components/reimagined/debt-journey-hero';
import LoanProgressCard from 'tio-employee/components/reimagined/loan-progress-card';
import MilestoneTracker from 'tio-employee/components/reimagined/milestone-tracker';
import type { DebtMilestone } from 'tio-employee/components/reimagined/milestone-tracker';
import SmartActionCard from 'tio-employee/components/reimagined/smart-action-card';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';
import type SessionContextService from 'tio-employee/services/session-context';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type PartnerService from 'tio-employee/services/partner';
import type RouterService from '@ember/routing/router-service';
import type { LiveSection127Info } from 'tio-employee/services/reimagined-data';
import type { EmployerContributions } from 'tio-employee/services/career-pathways-api';
import type AccountModel from 'tio-data/models/account';
import AccountSelectorDrawer from 'tio-employee/components/reimagined/account-selector-drawer';
import {
  PhBank,
  PhTrendUp,
  PhCalendar,
  PhLightbulb,
  PhArrowRight,
  PhArrowsLeftRight,
  PhCurrencyDollar,
  PhPercent,
  PhHandshake,
  PhTarget,
  PhChartLineUp,
  PhWarning,
  PhChatCircleDots,
  PhBookOpen,
  PhCloudArrowUp,
  PhPiggyBank,
  PhHeart,
} from 'ember-phosphor-icons';

export interface ReimaginedLoanSummaryModel {
  loans: LoanAccount[];
  hasLinkedLoans: boolean;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  section127Info: LiveSection127Info;
  hasStrategyFinderResults: boolean;
  /** Active accounts the user can switch contributions between */
  activeAccounts: AccountModel[];
  /** The account currently receiving employer contributions */
  accountReceivingContributions: AccountModel | null;
  /** Person ID for the account-change API call */
  personId: string | null;
}

interface ReimaginedLoanSummarySignature {
  Args: {
    model: ReimaginedLoanSummaryModel;
  };
}

const createProductionActions = (options: {
  section127Remaining: number;
  section127Used: number;
  hasStrategyFinderResults: boolean;
  formatCurrency: (amount: number) => string;
}): SmartAction[] => {
  const { section127Remaining, section127Used, hasStrategyFinderResults, formatCurrency } = options;
  const actions: SmartAction[] = [];

  actions.push({
    id: '1',
    priority: hasStrategyFinderResults ? 'medium' : 'high',
    category: 'money',
    icon: PhPercent,
    title: hasStrategyFinderResults
      ? 'Update your repayment strategy'
      : 'Find your optimal repayment strategy',
    description: hasStrategyFinderResults
      ? "Life changes. Review your strategy to ensure you're still on the best path to debt freedom."
      : "Based on your employment and loan profile, we can find the best path to debt freedom—whether that's PSLF, IDR, or aggressive payoff.",
    impact: 'Potentially save thousands',
    cta: hasStrategyFinderResults ? 'Review Strategy' : 'Run Strategy Finder',
    route: 'authenticated.repaying-student-debt.repayment-strategy-finder',
  });

  if (section127Remaining > 0) {
    actions.push({
      id: '2',
      priority: section127Remaining > 1000 ? 'high' : 'medium',
      category: 'opportunity',
      icon: PhHandshake,
      title: `${formatCurrency(section127Remaining)} of Section 127 unused`,
      description: `You've used ${formatCurrency(section127Used)} of your $5,250 tax-free benefit this year. Allocating the rest to loans could accelerate payoff.`,
      impact: 'Pay off faster',
      cta: 'Learn More',
      route: 'authenticated.repaying-student-debt.repayment-options',
    });
  }

  return actions;
};

export default class ReimaginedLoanSummary extends Component<ReimaginedLoanSummarySignature> {
  @service declare sessionContext: SessionContextService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare partner: PartnerService;
  @service declare router: RouterService;

  @tracked showAccountDrawer = false;

  get canChangeContributionAccount() {
    return (this.args.model.activeAccounts?.length ?? 0) > 1;
  }

  toggleAccountDrawer = () => {
    this.showAccountDrawer = !this.showAccountDrawer;
  };

  reloadAfterAccountChange = () => {
    this.router.refresh('authenticated.account-activity.accounts');
  };

  get loans() {
    return this.args.model.loans;
  }

  get hasLinkedLoans() {
    return this.args.model.hasLinkedLoans;
  }

  get isIdrEnabled() {
    return this.partner.isIdrEnabled;
  }

  get section127Info() {
    return this.args.model.section127Info;
  }

  get totalBalance() {
    return this.loans.reduce((sum, loan) => sum + loan.balance, 0);
  }

  get totalOriginalBalance() {
    return this.loans.reduce((sum, loan) => sum + loan.originalBalance, 0);
  }

  get totalMonthlyPayment() {
    return this.loans.reduce((sum, loan) => sum + loan.monthlyPayment, 0);
  }

  get progressPercent() {
    if (this.totalOriginalBalance <= 0) return 0;
    const paid = this.totalOriginalBalance - this.totalBalance;
    return Math.round((paid / this.totalOriginalBalance) * 100);
  }

  get yearProgressAmount() {
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      return contributions.contributions.yearToDate;
    }
    const paid = this.totalOriginalBalance - this.totalBalance;
    return paid > 0 ? paid : 0;
  }

  get estimatedPayoffDate() {
    if (this.loans.length === 0) return new Date().toISOString();
    const payoffDates = this.loans
      .map((loan) => loan.estimatedPayoff)
      .filter((date) => date)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    return payoffDates[0] || new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000).toISOString();
  }

  get employerContribution() {
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      const currentMonth = new Date().getMonth() + 1;
      return Math.round(contributions.contributions.yearToDate / currentMonth);
    }
    return 0;
  }

  get averageInterestRate() {
    if (!this.loans.length) return 0;
    const weighted = this.loans.reduce((sum, loan) => sum + loan.balance * loan.interestRate, 0);
    const total = this.loans.reduce((sum, loan) => sum + loan.balance, 0);
    return total > 0 ? Number((weighted / total).toFixed(1)) : 0;
  }

  get nextPaymentSubtext() {
    const next = this.loans
      .map((loan) => loan.nextPaymentDate)
      .filter((date) => date)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
    if (!next) return 'Next due: —';
    const formatted = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(new Date(next));
    return `Next due: ${formatted}`;
  }

  get employerName() {
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  get monthlyContribution() {
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      return Math.round(contributions.contributions.projectedAnnual / 12) || 0;
    }
    return 0;
  }

  get lifetimeBenefit() {
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      return contributions.contributions.lifetime || 0;
    }
    return 0;
  }

  get milestones(): DebtMilestone[] {
    const totalOriginal = this.totalOriginalBalance;
    const totalCurrent = this.totalBalance;
    if (totalOriginal <= 0) return [];

    const paidOff = totalOriginal - totalCurrent;
    const paidPercent = (paidOff / totalOriginal) * 100;

    return [
      {
        id: '1',
        title: 'First $5k',
        description: 'Paid first $5,000',
        threshold: totalOriginal > 5000 ? (5000 / totalOriginal) * 100 : 50,
        achieved: paidOff >= 5000,
        achievedDate: paidOff >= 5000 ? 'Achieved!' : undefined,
        icon: 'rocket',
      },
      {
        id: '2',
        title: '25% Done',
        description: 'Quarter paid off',
        threshold: 25,
        achieved: paidPercent >= 25,
        achievedDate: paidPercent >= 25 ? 'Achieved!' : undefined,
        icon: 'star',
      },
      {
        id: '3',
        title: 'Halfway',
        description: '50% to freedom',
        threshold: 50,
        achieved: paidPercent >= 50,
        icon: 'target',
      },
      {
        id: '4',
        title: '75% Done',
        description: 'Three quarters there',
        threshold: 75,
        achieved: paidPercent >= 75,
        icon: 'medal',
      },
      {
        id: '5',
        title: 'Debt Free!',
        description: '$0 balance',
        threshold: 100,
        achieved: totalCurrent <= 0,
        icon: 'crown',
      },
    ];
  }

  get actions() {
    return createProductionActions({
      section127Remaining: this.section127Info.remaining,
      section127Used: this.section127Info.yearToDateUsed,
      hasStrategyFinderResults: this.args.model.hasStrategyFinderResults,
      formatCurrency: formatCurrency,
    });
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{#if this.hasLinkedLoans}}
        {{! Hero Banner }}
        <DebtJourneyHero
          @currentBalance={{this.totalBalance}}
          @originalBalance={{this.totalOriginalBalance}}
          @estimatedPayoffDate={{this.estimatedPayoffDate}}
          @yearProgressAmount={{this.yearProgressAmount}}
        />

        {{! Key Metrics }}
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <StatCard
            @icon={{PhBank}}
            @label="Total Balance"
            @value={{formatCurrency this.totalBalance}}
            @color="indigo"
          >
            <:extra>
              <div class="flex items-center gap-1 tio-fg-success text-sm">
                <PhTrendUp @size={{14}} />
                <span>↓ {{formatCurrency this.yearProgressAmount}} this year</span>
              </div>
            </:extra>
          </StatCard>

          <StatCard
            @icon={{PhCalendar}}
            @label="Monthly Payment"
            @value="{{formatCurrency this.totalMonthlyPayment}}/mo"
            @subtext={{this.nextPaymentSubtext}}
            @color="blue"
          />

          <StatCard
            @icon={{PhHandshake}}
            @label="Employer Help"
            @value="{{formatCurrency this.employerContribution}}/mo"
            @subtext="Tax-free Section 127 benefit"
            @color="green"
            @valueColor="emerald"
          />

          <StatCard
            @icon={{PhPercent}}
            @label="Avg. Interest Rate"
            @value="{{this.averageInterestRate}}%"
            @color="amber"
          >
            <:extra>
              {{#if (eq this.averageInterestRate 0)}}
                <span></span>
              {{else}}
                <div class="flex items-center gap-1 tio-fg-warning-body text-sm">
                  <PhWarning @size={{14}} />
                  <span>Refinance may lower this</span>
                </div>
              {{/if}}
            </:extra>
          </StatCard>
        </div>

        {{! Smart Actions }}
        {{#if this.actions.length}}
          <div class="mt-6">
            <SectionHeader
              @icon={{PhLightbulb}}
              @title="Smart Actions"
              @iconClass="tio-fg-warning"
              @iconSize={{24}}
            />
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              {{#each this.actions as |smartAction|}}
                <SmartActionCard @item={{smartAction}} />
              {{/each}}
            </div>
          </div>
        {{/if}}

        {{! Loan Accounts }}
        <div class="mt-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <PhBank @size={{24}} class="tio-fg-secondary" />
              <Heading @level={{2}} @size="lg">Your Loan Accounts</Heading>
            </div>
            <div class="flex items-center gap-3">
              {{#if this.canChangeContributionAccount}}
                <button
                  type="button"
                  class="flex items-center gap-1 text-sm font-medium tio-fg-success hover:tio-fg-success-h focus-visible:outline-none focus-visible:ring-2 focus-visible:tio-ring-success focus-visible:ring-offset-2 rounded"
                  {{on "click" this.toggleAccountDrawer}}
                >
                  <PhArrowsLeftRight @size={{16}} />
                  <span>Change Contribution Account</span>
                </button>
              {{/if}}
              <LinkTo
                @route="authenticated.observability.upload"
                @query={{hash
                  source="loan-summary"
                  providers="nslds,statement"
                  returnTo="authenticated.account-activity.accounts"
                }}
                class="flex items-center gap-1 text-sm font-medium tio-fg-brand hover:tio-fg-brand-accent"
              >
                <span>Add Account</span>
                <PhArrowRight @size={{16}} />
              </LinkTo>
            </div>
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {{#each this.loans as |loan index|}}
              <LoanProgressCard
                @loan={{loan}}
                @featured={{eq index 0}}
                @detailRoute="authenticated.account-activity.loans.detail"
              />
            {{/each}}
          </div>
        </div>

        {{! Milestones }}
        {{#if this.milestones.length}}
          <div class="mt-6">
            <MilestoneTracker
              @milestones={{this.milestones}}
              @currentProgress={{this.progressPercent}}
            />
          </div>
        {{/if}}

        {{! Financial Tools }}
        <div class="mt-6">
          <SectionHeader
            @icon={{PhChartLineUp}}
            @title="Financial Tools"
            @iconClass="tio-fg-brand"
            @iconSize={{24}}
          />
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <NavCard
              @layout="gradient"
              @route="authenticated.repaying-student-debt.repayment-strategy-finder"
              @icon={{PhTarget}}
              @title="Strategy Finder"
              @subtitle="Best repayment path"
              @intent="info"
            />
            <NavCard
              @layout="gradient"
              @route="authenticated.repaying-student-debt.coaching"
              @icon={{PhChatCircleDots}}
              @title="Loan Coaches"
              @subtitle="Expert guidance"
              @intent="brand"
            />
            <NavCard
              @layout="gradient"
              @route="authenticated.repaying-student-debt.repayment-options"
              @icon={{PhBookOpen}}
              @title="Repayment Options"
              @subtitle="Compare plans"
              @intent="info"
            />
            {{#if this.isIdrEnabled}}
              <NavCard
                @layout="gradient"
                @route="authenticated.idr.dashboard"
                @icon={{PhCurrencyDollar}}
                @title="Income-Driven"
                @subtitle="Lower payments"
                @intent="warning"
              />
            {{else if this.reimaginedData.showFinancialWellness}}
              <NavCard
                @layout="gradient"
                @route="authenticated.wellness-insights"
                @icon={{PhHeart}}
                @title="Financial Wellness"
                @subtitle="Build healthy habits"
                @intent="danger"
              />
            {{else}}
              <NavCard
                @layout="gradient"
                @route="authenticated.planning-for-college.529-tools"
                @icon={{PhPiggyBank}}
                @title="529 Plans"
                @subtitle="Save for education"
                @intent="danger"
              />
            {{/if}}
          </div>
        </div>

        {{! Employer Contribution Banner }}
        {{#if this.lifetimeBenefit}}
          <div class="tio-bg-success-gradient rounded-xl p-6 text-white mt-6">
            <div class="flex items-center justify-between flex-wrap gap-4">
              <div class="flex items-center gap-4">
                <div class="p-3 bg-white/20 rounded-lg">
                  <PhHandshake @size={{28}} />
                </div>
                <div>
                  <Heading @level={{3}} class="text-white">
                    {{this.employerName}}
                    —
                    {{formatCurrency this.lifetimeBenefit}}
                    applied toward your loans
                  </Heading>
                  <Text class="text-white/90">
                    Part of your benefits package, applied directly to your student loan balance.
                  </Text>
                </div>
              </div>
              <LinkTo @route="authenticated.slr.dashboard">
                <Button
                  @intent="primary"
                  @appearance="outlined"
                  class="border-white text-white hover:bg-white/10"
                >
                  View Contribution History
                </Button>
              </LinkTo>
            </div>
          </div>
        {{/if}}

        {{! Account Selector Drawer }}
        {{#if this.canChangeContributionAccount}}
          <AccountSelectorDrawer
            @isOpen={{this.showAccountDrawer}}
            @onClose={{this.toggleAccountDrawer}}
            @accounts={{@model.activeAccounts}}
            @currentAccount={{@model.accountReceivingContributions}}
            @personId={{@model.personId}}
            @onAccountChanged={{this.reloadAfterAccountChange}}
          />
        {{/if}}
      {{else}}
        {{! NO LOANS LINKED YET }}
        <div class="tio-bg-info-brand-wash rounded-2xl p-8 border tio-border-info text-center">
          <div
            class="w-20 h-20 tio-bg-info-soft rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <PhCloudArrowUp @size={{40}} class="tio-fg-info" />
          </div>

          <Heading @level={{1}} class="app-page-title mb-3">
            Upload Your Student Loan Documents
          </Heading>

          <Text class="tio-fg-secondary max-w-md mx-auto mb-6">
            Upload your loan statements or NSLDS file to see your balances, track your progress, and
            start receiving employer contributions.
          </Text>

          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <LinkTo
              @route="authenticated.observability.upload"
              @query={{hash
                source="loan-summary"
                providers="nslds,statement"
                returnTo="authenticated.account-activity.accounts"
              }}
              class="inline-flex items-center justify-center gap-2 rounded-md tio-bg-brand px-4 py-2 text-sm font-medium tio-fg-on-brand shadow-sm hover:tio-bg-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:tio-ring-brand focus-visible:ring-offset-2"
            >
              <PhCloudArrowUp @size={{18}} />
              <span>Upload Loan Documents</span>
            </LinkTo>
          </div>

          <div class="mt-6 tio-bg-surface-translucent rounded-xl p-4 max-w-md mx-auto">
            <Text class="text-sm tio-fg-muted">
              Accepted: NSLDS files from StudentAid.gov, loan servicer statements, or any document
              showing your loan details.
            </Text>
          </div>

          {{#if this.monthlyContribution}}
            <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
              <div class="tio-bg-surface rounded-xl p-4">
                <div class="text-2xl font-bold tio-fg-brand">
                  {{formatCurrency this.monthlyContribution}}
                </div>
                <div class="text-sm tio-fg-secondary">Monthly employer contribution</div>
              </div>
              <div class="tio-bg-surface rounded-xl p-4">
                <div class="text-2xl font-bold tio-fg-success">Tax-free</div>
                <div class="text-sm tio-fg-secondary">Section 127 benefit</div>
              </div>
              <div class="tio-bg-surface rounded-xl p-4">
                <div class="text-2xl font-bold tio-fg-brand">
                  {{formatCurrency this.lifetimeBenefit}}
                </div>
                <div class="text-sm tio-fg-secondary">Lifetime employer benefit</div>
              </div>
            </div>
          {{/if}}
        </div>
      {{/if}}
    </VStack>
  </template>
}
